/**
 * 🪪 Hero Window — owner-approved design (Feb 2026).
 *
 * Layout (owner spec, verbatim):
 *   • Header: Name + TIER badge + VIP pin (gold when VIP, grey when not).
 *     Below: Handle, then "CHARACTER", then Lv · Guild · Battle Rating · VIP.
 *   • Center: hero pedestal (placeholder figure — final art later).
 *   • Left column (GEAR): Helmet / Chest / Wings / Talon / Aura.
 *   • Right column (WEAPONS): Main Hand / Offhand / HP / HP / DFCL.
 *     3rd right box renders "HP" but is NOT HP (content still open).
 *   • Pins flanking the panel (left top→down: Battlefield Cards, DragonHeir,
 *     Wardrobe, Area/Arena; right top→down: Inner Light Crystals (+ add
 *     badge), Skill Tree, Heroes Codex, Charisma bar).
 *   • Bottom row: EMBLEM / HALIDOM / WEDDING RING.
 *   • Drawer tabs (vertical, left-edge of drawer): INVENTORY · EQUIPMENT ·
 *     STATS · MP-CRYSTALS · TITLE. STATS tab = 14 stats grid.
 *   • Drawer IS Collection Breadth; INVENTORY is the open drawer; Clothing
 *     IS the Wardrobe. ONE Battle Power number above the hero — Aether/Ash
 *     split lives on the leaderboard, not here. NO Guard element.
 *
 * All data via GET /api/hero-window.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import InnerLightUpgradeCard from "@/components/InnerLightUpgradeCard";
import HeroMount, { MountTab } from "@/components/HeroMount";
import Tilt3D from "@/components/Tilt3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  const v = Number(n || 0);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

const DRAWER_TABS = [
  { id: "inventory",  label: "INVENTORY" },
  { id: "equipment",  label: "EQUIPMENT" },
  { id: "stats",      label: "STATS" },
  { id: "mp",         label: "MP-CRYSTALS" },
  { id: "mount",      label: "MOUNT" },
  { id: "title",      label: "TITLE" },
];

export default function HeroWindowPage() {
  const nav = useNavigate();
  const [data,    setData]    = useState(null);
  const [err,     setErr]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerTab, setDrawerTab] = useState("inventory");
  const [showInnerLight, setShowInnerLight] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hero-window`, { withCredentials: true });
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load hero window.");
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#1a0e1e] text-amber-100">
        <Header />
        <div className="p-10 text-center text-amber-300">Loading hero window…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#1a0e1e] text-amber-100">
        <Header />
        <div className="p-10 text-center text-red-300">{err}</div>
      </div>
    );
  }
  if (!data) return null;

  const h    = data.header   || {};
  const tier = h.tier || {};
  const gear = data.gear || {};
  const stats = data.stats_grid || [];
  const invItems = data.inventory?.items || [];
  const invSlots = data.inventory?.slots || 10;
  const pins = data.pins || { left: [], right: [] };
  const mpc  = data.mp_crystals || {};

  // Padded inventory grid to the owner-specified 10 slots (6 real + 4 empty).
  const padded = Array.from({ length: invSlots }, (_, i) => invItems[i] || null);

  return (
    <div className="min-h-screen bg-[#1a0e1e] text-amber-100" data-testid="hero-window-page">
      <Header />
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-5">
        {/* ─── Top header ───────────────────────────────────────────── */}
        <HeroHeader h={h} tier={tier} />

        {/* ─── Three-column body + drawer ──────────────────────────── */}
        <div className="grid grid-cols-12 gap-3 mt-4">
          {/* LEFT: Gear column */}
          <aside className="col-span-12 sm:col-span-2 flex sm:block" data-testid="hero-gear-col">
            <SectionLabel>GEAR</SectionLabel>
            <SlotColumn slots={[
              { slot: gear.helmet, label: "HELMET" },
              { slot: gear.chest,  label: "CHEST"  },
              { slot: gear.wings,  label: "WINGS"  },
              { slot: gear.talon,  label: "TALON"  },
              { slot: gear.aura,   label: "AURA"   },
            ]} vertTabs={["INVENTORY","EQUIPMENT","STATS","MP-CRYSTALS","MOUNT","TITLE"]}
               onTab={(id)=>setDrawerTab(id.toLowerCase().split("-")[0] === "mp" ? "mp" : id.toLowerCase())}/>
          </aside>

          {/* CENTER: Hero pedestal + info line + pins */}
          <section className="col-span-12 sm:col-span-6 relative" data-testid="hero-center-col">
            <div className="text-center text-[10px] uppercase tracking-widest text-amber-300/70 mb-1">
              CHARACTER
            </div>
            <div className="text-center text-[11px] text-neutral-300 mb-2 flex flex-wrap justify-center gap-x-3 gap-y-0.5">
              <span data-testid="hero-info-level">Lv. {h.level || "—"}</span>
              <span className="text-neutral-600">·</span>
              <span data-testid="hero-info-guild">Guild: {h.guild_name || (h.guild_id ? h.guild_id : "— none —")}</span>
              <span className="text-neutral-600">·</span>
              <span data-testid="hero-info-br">
                Battle Rating: <span className="text-amber-300 font-mono font-bold">{fmt(h.battle_rating)}</span>
              </span>
              <span className="text-neutral-600">·</span>
              <span className={h.is_vip ? "text-amber-300" : "text-neutral-600"}>VIP</span>
            </div>

            <div className="relative mx-auto max-w-md" data-testid="hero-pedestal-stage">
              {/* Pins — left edge */}
              <div className="absolute -left-5 sm:-left-7 top-6 flex flex-col gap-3 z-10">
                {pins.left.map((p) => <Pin key={p.id} pin={p} nav={nav} />)}
              </div>
              {/* Pins — right edge */}
              <div className="absolute -right-5 sm:-right-7 top-6 flex flex-col gap-3 z-10">
                {pins.right.map((p) => (
                  <Pin
                    key={p.id} pin={p} nav={nav}
                    onInnerLight={() => setShowInnerLight(true)}
                  />
                ))}
              </div>

              {/* Pedestal panel */}
              <div
                className="aspect-[3/4] bg-[#faf6ec] rounded-sm border-2 border-amber-700/40 flex flex-col items-center justify-end relative overflow-hidden"
                data-testid="hero-pedestal"
              >
                {/* 🖼️ Owner-directive avatar — real hero sprite riding
                    the equipped mount when available (visual-only; stats
                    come from the Mount tab / stat chip). Falls back
                    gracefully for classes without art yet. */}
                <HeroMount
                  heroName={h.name}
                  className={h.class_name}
                  sprite_url={h.sprite_url}
                />
                {/* Pedestal ellipse + Area/Arena + Charisma labels */}
                <div className="w-full px-4 pb-3 flex items-end justify-between relative">
                  <span className="text-[9px] uppercase tracking-widest text-neutral-500">
                    🟡 Area/Arena
                  </span>
                  <div className="absolute left-1/2 bottom-1 -translate-x-1/2 w-40 h-7 rounded-full bg-neutral-200 shadow-inner" />
                  <span className="text-[9px] uppercase tracking-widest text-neutral-500 relative z-10 flex items-center gap-1">
                    Charisma 🟡
                  </span>
                </div>
                <div className="w-full flex justify-end px-4 pb-2 relative z-10">
                  <CharismaBar value={pins.right.find((p) => p.id === "charisma")?.value || 0}
                               max={pins.right.find((p) => p.id === "charisma")?.max || 100} />
                </div>
              </div>
            </div>
          </section>

          {/* RIGHT: Weapons column */}
          <aside className="col-span-12 sm:col-span-2" data-testid="hero-weapons-col">
            <SectionLabel>WEAPONS</SectionLabel>
            <SlotColumn slots={[
              { slot: gear.main_hand, label: "MAIN HAND" },
              { slot: gear.offhand,   label: "OFFHAND"   },
              { slot: gear.hp_1,      label: "HP"        },
              // Owner spec: renders "HP" but is NOT HP — content still open.
              { slot: gear.hp_2,      label: "HP"        },
              { slot: gear.dfcl,      label: "DFCL"      },
            ]} />
          </aside>

          {/* FAR RIGHT: Drawer */}
          <aside className="col-span-12 sm:col-span-2" data-testid="hero-drawer-col">
            <Drawer
              activeTab={drawerTab}
              setActiveTab={setDrawerTab}
              inventory={padded}
              stats={stats}
              gear={gear}
              mpc={mpc}
              h={h}
            />
          </aside>
        </div>

        {/* ─── Bottom row: EMBLEM · HALIDOM · WEDDING RING ──────────── */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 bg-black/50 border border-amber-700/40 rounded-full px-4 py-2 flex items-center justify-around gap-3">
            <SlimPill slot={gear.emblem}        label="EMBLEM" />
            <span className="w-px h-5 bg-amber-800/40" />
            <SlimPill slot={gear.halidom}       label="HALIDOM" />
            <span className="w-px h-5 bg-amber-800/40" />
            <SlimPill slot={gear.wedding_ring}  label="WEDDING RING" />
          </div>
          <button
            data-testid="hero-sale-btn"
            className="bg-black/50 border border-amber-700/40 text-amber-200 font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:border-amber-500 rounded-sm"
          >
            Sale
          </button>
          <button
            data-testid="hero-sort-btn"
            className="bg-black/50 border border-amber-700/40 text-amber-200 font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:border-amber-500 rounded-sm"
          >
            Sort
          </button>
        </div>

        {/* Inner Light overlay (triggered by the pin's "+" badge) */}
        {showInnerLight && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowInnerLight(false)}
            data-testid="inner-light-modal"
          >
            <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <InnerLightUpgradeCard onUpgraded={() => refresh()} />
              <button
                onClick={() => setShowInnerLight(false)}
                className="w-full mt-2 bg-zinc-900 hover:bg-zinc-800 text-neutral-400 py-2 uppercase tracking-widest text-xs border border-zinc-800"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// ─────────────────────── Pieces ───────────────────────

function HeroHeader({ h, tier }) {
  return (
    <div className="text-center" data-testid="hero-header">
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <h1 className="font-display text-4xl sm:text-5xl font-black text-amber-300 tracking-wide"
            data-testid="hero-name">
          {(h.name || "—").toUpperCase()}
        </h1>
        <span
          className="px-3 py-1 border-2 border-amber-500 text-amber-200 text-[11px] uppercase tracking-widest rounded-sm font-bold"
          data-testid="hero-tier-badge"
          title={tier.flavor || ""}
        >
          Tier {tier.sub ? ` ${tier.sub}` : " —"}
        </span>
        <span
          className={
            "px-3 py-1 border-2 text-[11px] uppercase tracking-widest rounded-full font-bold " +
            (h.is_vip
              ? "border-amber-400 text-amber-300 bg-amber-900/30"
              : "border-neutral-600 text-neutral-500")
          }
          data-testid="hero-vip-pin"
        >
          VIP
        </span>
      </div>
      <div className="font-serif italic text-amber-100/90 text-xl mt-1" data-testid="hero-handle">
        {h.handle || "—"}
      </div>
      {tier.label && (
        <div className="text-[10px] uppercase tracking-widest text-amber-300/70 mt-1"
             data-testid="hero-tier-label">
          {tier.label}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-center text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">
      {children}
    </div>
  );
}

function SlotColumn({ slots }) {
  return (
    <div className="space-y-2">
      {slots.map((row, i) => (
        <GearBox key={i} slot={row.slot} label={row.label} note={row.note} />
      ))}
    </div>
  );
}

function GearBox({ slot, label, note }) {
  const empty = !slot || !slot.item_id;
  const key = (slot?.slot) || label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      className="relative bg-black/50 border border-amber-700/40 h-20 flex items-center justify-center rounded-sm hover:border-amber-500 transition"
      data-testid={`gear-slot-${key}`}
    >
      {/* Round item dot (top-left) */}
      <span className={"absolute left-2 top-2 w-5 h-5 rounded-full " +
        (empty ? "bg-black/40 border border-amber-800/40" : "bg-amber-400")} />
      {/* +— enhancement badge (values unset per owner spec) */}
      <span className="absolute right-2 top-1 text-[9px] text-amber-300/70 border border-amber-800/50 rounded px-1 bg-black/40">
        +—
      </span>
      <span className="font-bold text-amber-200 text-[13px] uppercase tracking-widest">
        {label}
      </span>
      {note && (
        <span className="absolute bottom-1 right-1 text-[7px] uppercase tracking-widest text-neutral-500">
          {note}
        </span>
      )}
    </div>
  );
}

function SlimPill({ slot, label }) {
  const key = (slot?.slot) || label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="flex-1 text-center text-amber-200 font-bold uppercase tracking-widest text-[11px]"
         data-testid={`bottom-slot-${key}`}>
      {label}
    </div>
  );
}

function Pin({ pin, nav, onInnerLight }) {
  const handle = () => {
    if (pin.id === "inner_light") { onInnerLight?.(); return; }
    if (pin.route) nav(pin.route);
  };
  const isInnerLight = pin.id === "inner_light";
  const isCharisma   = pin.id === "charisma";
  return (
    <button
      onClick={handle}
      disabled={!pin.route && !isInnerLight && !isCharisma}
      title={pin.label}
      data-testid={`pin-${pin.id}`}
      className={
        "relative w-10 h-10 rounded-full border flex items-center justify-center text-lg " +
        "transition shrink-0 " +
        (isInnerLight
          ? "border-purple-400 bg-purple-900/60 text-purple-200 hover:bg-purple-800"
          : "border-amber-700/40 bg-black/60 text-amber-200 hover:border-amber-400")
      }
    >
      <span>{pin.icon}</span>
      {isInnerLight && (
        <span
          className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-amber-400 text-black text-[11px] font-black flex items-center justify-center shadow"
          data-testid="pin-inner-light-add-badge"
        >
          +
        </span>
      )}
      {typeof pin.badge === "number" && pin.badge > 0 && (
        <span className="absolute -right-1 -top-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[9px] font-black flex items-center justify-center">
          {pin.badge}
        </span>
      )}
    </button>
  );
}

function CharismaBar({ value, max }) {
  const pct = Math.max(0, Math.min(100, (Number(value || 0) / Math.max(1, Number(max || 100))) * 100));
  return (
    <div className="w-24 h-2 bg-neutral-300 rounded-full overflow-hidden" title={`${value}/${max}`}>
      <div className="h-full bg-[#7c3aed]" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * 🖼️ HeroAvatarArt — real class sprite on the pedestal.
 *
 * Owner directive Feb 2026: show the actual avatar. We try the hero
 * codex `sprite_url` first; if the image 404s (or the class has no
 * art yet) we fall back to a clean silhouette with the class name,
 * so the window never shows broken art and never shows a dev note.
 */
function HeroAvatarArt({ sprite_url, class_name }) {
  const [broken, setBroken] = useState(false);
  const canShowImage = sprite_url && !broken;
  return (
    <div className="flex-1 flex items-end justify-center pb-5 relative w-full">
      {canShowImage ? (
        <img
          src={sprite_url}
          alt={class_name || "Hero avatar"}
          className="max-h-[85%] object-contain drop-shadow-md"
          onError={() => setBroken(true)}
          data-testid="hero-avatar-img"
        />
      ) : (
        <div className="relative flex flex-col items-center" aria-hidden="true" data-testid="hero-avatar-silhouette">
          <div className="w-14 h-14 rounded-full bg-[#9b7557]" />
          <div className="w-24 h-28 bg-[#9b7557] rounded-md mt-1 relative">
            <span className="absolute -left-5 top-2 w-5 h-14 bg-[#9b7557] rounded-md" />
            <span className="absolute -right-5 top-2 w-5 h-14 bg-[#9b7557] rounded-md" />
          </div>
          <div className="flex gap-1 mt-1">
            <span className="w-5 h-7 bg-[#9b7557] rounded-sm" />
            <span className="w-5 h-7 bg-[#9b7557] rounded-sm" />
          </div>
          {class_name && (
            <div className="absolute bottom-0 translate-y-4 text-[9px] uppercase tracking-widest text-neutral-500">
              {class_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Drawer ───────────────────────

function Drawer({ activeTab, setActiveTab, inventory, stats, gear, mpc, h }) {
  return (
    <div className="relative bg-black/50 border border-amber-700/40 rounded-sm" data-testid="hero-drawer">
      {/* Left vertical tab strip */}
      <div className="absolute -left-6 top-6 flex flex-col gap-1">
        {DRAWER_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`drawer-tab-${t.id}`}
            className={
              "writing-vertical text-[9px] uppercase tracking-widest font-bold px-1 py-1.5 border rounded-sm transition " +
              (activeTab === t.id
                ? "border-amber-400 text-amber-300 bg-amber-900/30"
                : "border-amber-800/40 text-amber-300/60 bg-black/40 hover:text-amber-200")
            }
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panel header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-700/40">
        <div className="font-bold text-amber-300 uppercase tracking-widest text-[11px]"
             data-testid="drawer-active-label">
          {DRAWER_TABS.find((t) => t.id === activeTab)?.label || "INVENTORY"}
        </div>
        <button
          data-testid="drawer-help"
          className="w-5 h-5 rounded-full border border-amber-600/60 text-amber-300 text-[11px] font-bold hover:border-amber-400"
          title="Collection Breadth. Drawer tabs are the lifetime roll-up: Inventory bags, Equipment slots, Stats grid, Mother Phoenix Crystals, and your Title."
        >?</button>
      </div>

      {/* Tab content */}
      <div className="p-2">
        {activeTab === "inventory"  && <InventoryGrid items={inventory} />}
        {activeTab === "equipment"  && <EquipmentList gear={gear} />}
        {activeTab === "stats"      && <StatsGrid     stats={stats} />}
        {activeTab === "mp"         && <MpCrystalsTab mpc={mpc} />}
        {activeTab === "mount"      && <MountTab heroName={h?.name} className={h?.class_name} />}
        {activeTab === "title"      && <TitleTab      h={h} />}
      </div>
    </div>
  );
}

function InventoryGrid({ items }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="drawer-inventory">
      {items.map((it, i) => (
        <div
          key={it?.item_id || `empty-${i}`}
          className={
            "aspect-square border rounded-sm flex flex-col items-center justify-center p-1 text-center relative " +
            (it ? "border-amber-700/40 bg-black/40" : "border-amber-800/20 bg-black/20")
          }
          data-testid={it ? `inv-${it.item_id}` : `inv-empty-${i}`}
        >
          {it ? (
            <>
              {it.art_url ? (
                <Tilt3D max={16} scale={1.08} className="w-10 h-10 relative">
                  <img
                    src={it.art_url}
                    alt={it.name}
                    className="w-10 h-10 object-contain"
                    loading="lazy"
                    data-testid={`inv-${it.item_id}-art`}
                  />
                </Tilt3D>
              ) : (
                <div className="text-2xl">{it.icon}</div>
              )}
              <div className="text-[9px] text-amber-200 font-bold leading-tight mt-1">
                {it.name}
              </div>
              <div className="text-[9px] text-amber-300/70 leading-tight">
                {it.qty_label}
              </div>
            </>
          ) : (
            <div className="text-[10px] uppercase tracking-widest text-amber-800/50">EMPTY</div>
          )}
        </div>
      ))}
    </div>
  );
}

function EquipmentList({ gear }) {
  const rows = Object.values(gear || {});
  return (
    <ul className="space-y-1 text-[11px]" data-testid="drawer-equipment">
      {rows.map((r, i) => (
        <li key={r.slot || i} className="flex justify-between px-1 py-1 border-b border-amber-900/30">
          <span className="uppercase tracking-widest text-amber-300/70">{(r.slot || "").replace("_"," ")}</span>
          <span className={r.item_id ? "text-amber-100" : "text-neutral-600 italic"}>
            {r.name || "empty"}
          </span>
        </li>
      ))}
    </ul>
  );
}

function StatsGrid({ stats }) {
  // Drawer STATS tab now folds the old /hero/stats sheet detail
  // (owner directive Feb 2026). Each row shows base → final with the
  // delta coloured when a boost is applied.
  return (
    <ul className="space-y-1 text-[10px]" data-testid="drawer-stats">
      {stats.map((s) => {
        const hasBase = typeof s.base === "number";
        const hasVal  = typeof s.value === "number";
        const d       = typeof s.delta === "number" ? s.delta : null;
        return (
          <li
            key={s.key}
            className={
              "border rounded-sm px-2 py-1 flex items-center justify-between " +
              (s.present ? "border-amber-700/40 bg-black/40 text-amber-100"
                         : "border-amber-900/20 bg-black/20 text-neutral-600")
            }
            data-testid={`stat-${s.key}`}
          >
            <span className="uppercase tracking-widest">{s.label}</span>
            {s.present ? (
              <span className="font-mono tabular-nums flex items-center gap-1">
                {hasBase && hasVal && d !== 0 && (
                  <span className="text-neutral-500 line-through">{fmt(Math.round(s.base))}</span>
                )}
                <span>{fmt(Math.round(s.value * 100) / 100)}</span>
                {d !== null && d !== 0 && (
                  <span className={d > 0 ? "text-emerald-400" : "text-red-400"}>
                    ({d > 0 ? "+" : ""}{fmt(d)})
                  </span>
                )}
              </span>
            ) : (
              <span className="font-mono tabular-nums">—</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function MpCrystalsTab({ mpc }) {
  return (
    <div className="space-y-2 text-[11px]" data-testid="drawer-mp">
      <Row label="Equipped Crystals"  value={fmt(mpc.equipped)} />
      <Row label="Inner Light Level"  value={`Lv ${mpc.inner_light_level ?? 0}`} />
      <Row label="Inner Light Wallet" value={`${fmt(mpc.inner_light_crystals)} 💎`} />
      <div className="text-[10px] text-neutral-500 italic">
        Each Inner Light level grants +2% to every equipped Mother Phoenix Crystal, capped at +200%.
      </div>
    </div>
  );
}

function TitleTab({ h }) {
  return (
    <div className="space-y-2 text-[11px]" data-testid="drawer-title">
      <Row label="Tier"   value={h?.tier?.label || "—"} />
      <Row label="Handle" value={h?.handle || "—"} />
      <Row label="Guild"  value={h?.guild_name || (h?.guild_id ? "—" : "—")} />
      <Row label="VIP"    value={h?.is_vip ? "Active" : "—"} />
      <div className="text-[10px] text-neutral-500 italic">
        Battleground titles are won in combat. Lose honor, lose the title; climb back and reclaim it.
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between px-1 py-1 border-b border-amber-900/30">
      <span className="uppercase tracking-widest text-amber-300/70">{label}</span>
      <span className="text-amber-100">{value}</span>
    </div>
  );
}
